import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import { API_URL } from '../config';
import {
  Baby,
  Sparkles,
  Search,
  AlertTriangle,
  Info,
  Syringe,
  Droplet,
  Clock,
  Shield,
  Brain,
  Heart,
  Activity,
  Zap,
  Lock,
  Thermometer,
  Scale
} from 'lucide-react';
import { toast } from 'sonner';

export default function PrescriptionPediatriePage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMed, setSelectedMed] = useState(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [loading, setLoading] = useState(false);

  // Organisation des médicaments pédiatriques par catégorie
  const medicationCategories = {
    cardiovascular: {
      name: "Cardiovasculaires",
      icon: <Heart className="w-5 h-5" />,
      color: "bg-red-500/10 text-red-400 border-red-500/30",
      medications: [
        {
          name: "Adrénaline (Épinéphrine)",
          indication: "Arrêt cardiaque, choc anaphylactique, bradycardie sévère",
          preparation: "À diluer selon protocole",
          posologie: "AC: 0,01 mg/kg (0,1 mL/kg de solution 1/10000) toutes les 3-5 min. Anaphylaxie: 0,01 mg/kg IM",
          voie: "IV / IM / Intraosseuse",
          alert: "Vérifier concentration - Risque d'erreur médicamenteuse"
        },
        {
          name: "Noradrénaline",
          indication: "Choc septique, hypotension réfractaire",
          preparation: "PSE à diluer",
          posologie: "0,05 à 2 µg/kg/min en PSE. Titrer selon PAM et clinique",
          contrIndications: "Hypovolémie non corrigée",
          effetsSecondaires: "Hypertension, bradycardie, nécrose cutanée si extravasation",
          voie: "PSE / Voie centrale préférée",
          alert: "Surveillance PA continue - Voie centrale recommandée"
        },
        {
          name: "Dobutamine",
          indication: "Insuffisance cardiaque, choc cardiogénique",
          preparation: "PSE",
          posologie: "2,5 à 20 µg/kg/min. Débuter à 5 µg/kg/min et titrer",
          effetsSecondaires: "Tachycardie, arythmies, hypotension",
          voie: "PSE"
        },
        {
          name: "Dopamine",
          indication: "Hypotension, bradycardie",
          preparation: "PSE",
          posologie: "2-20 µg/kg/min selon effet recherché. Inotrope: 5-15 µg/kg/min",
          voie: "PSE"
        }
      ]
    },
    sedationAnalgesie: {
      name: "Sédation & Analgésie",
      icon: <Brain className="w-5 h-5" />,
      color: "bg-indigo-500/10 text-indigo-400 border-indigo-500/30",
      medications: [
        {
          name: "Midazolam (Hypnovel®)",
          indication: "Sédation, état de mal convulsif",
          preparation: "Pur ou à diluer",
          posologie: "Sédation: 0,05-0,15 mg/kg IV lente ou 0,2-0,5 mg/kg IN. État de mal: 0,15 mg/kg",
          contrIndications: "Insuffisance respiratoire sévère, glaucome angle fermé",
          effetsSecondaires: "Dépression respiratoire, apnée, hypotension",
          voie: "IV / Intranasale / Per os",
          alert: "Antidote: Flumazénil - Surveiller FR et SpO2"
        },
        {
          name: "Kétamine",
          indication: "Analgésie, sédation procédurale, induction",
          preparation: "À diluer",
          posologie: "Analgésie: 0,5-1 mg/kg IV. Sédation: 1-2 mg/kg IV. IN: 3-5 mg/kg",
          effetsSecondaires: "Hallucinations, hypersalivation, nystagmus, vomissements",
          voie: "IV / IM / Intranasale"
        },
        {
          name: "Morphine Chlorhydrate",
          indication: "Douleur modérée à sévère",
          preparation: "À diluer",
          posologie: "0,05-0,1 mg/kg toutes les 4-6h. Titration: 0,025 mg/kg toutes les 5 min",
          effetsSecondaires: "Dépression respiratoire, nausées, vomissements, constipation, prurit",
          voie: "IV / SC",
          alert: "Antidote: Naloxone - Surveillance respiratoire"
        },
        {
          name: "Fentanyl",
          indication: "Analgésie intense, sédation procédurale",
          preparation: "Pur ou à diluer",
          posologie: "1-2 µg/kg en bolus. PSE: 1-5 µg/kg/h",
          effetsSecondaires: "Rigidité thoracique, dépression respiratoire, bradycardie",
          voie: "IV / Intranasale / PSE",
          alert: "Puissant opioïde - Surveillance étroite"
        },
        {
          name: "Propofol (Diprivan®)",
          indication: "Sédation procédurale, induction anesthésie",
          preparation: "Pur",
          posologie: "Induction: 2-3 mg/kg IV lente. Sédation: 1-2 mg/kg puis PSE 50-200 µg/kg/min",
          contrIndications: "< 1 mois, allergie œuf/soja, insuffisance cardiaque sévère",
          effetsSecondaires: "Hypotension, dépression respiratoire, apnée, douleur injection",
          voie: "IV / PSE",
          alert: "Surveillance continue PA, FR, SpO2"
        }
      ]
    },
    anticonvulsivants: {
      name: "Anticonvulsivants",
      icon: <Zap className="w-5 h-5" />,
      color: "bg-pink-500/10 text-pink-400 border-pink-500/30",
      medications: [
        {
          name: "Diazépam (Valium®)",
          indication: "État de mal épileptique, convulsions",
          preparation: "Pur",
          posologie: "0,3-0,5 mg/kg IV ou IR (max 10 mg). Répéter 1 fois après 5-10 min",
          effetsSecondaires: "Somnolence, dépression respiratoire, hypotension",
          voie: "IV / Intra-rectale"
        },
        {
          name: "Phénobarbital (Gardénal®)",
          indication: "État de mal épileptique réfractaire",
          preparation: "À reconstituer",
          posologie: "15-20 mg/kg en IVL sur 15-20 min. Entretien: 2-5 mg/kg/j",
          contrIndications: "Insuffisance respiratoire, porphyrie",
          effetsSecondaires: "Sédation profonde, dépression respiratoire, hypotension",
          voie: "IV / IM",
          alert: "Surveillance respiratoire continue"
        },
        {
          name: "Valproate de sodium (Dépakine®)",
          indication: "État de mal épileptique",
          preparation: "À diluer",
          posologie: "20-40 mg/kg en IVL sur 5 min (max 3g)",
          contrIndications: "Grossesse, hépatopathie, porphyrie",
          effetsSecondaires: "Tremblements, hyperammoniémie, troubles hépatiques",
          voie: "IV"
        }
      ]
    },
    antipyretiques: {
      name: "Antipyrétiques / Anti-inflammatoires",
      icon: <Thermometer className="w-5 h-5" />,
      color: "bg-orange-500/10 text-orange-400 border-orange-500/30",
      medications: [
        {
          name: "Paracétamol (Doliprane®, Perfalgan®)",
          indication: "Fièvre, douleur légère à modérée",
          preparation: "Prêt à l'emploi (poche) ou à diluer",
          posologie: "15 mg/kg/6h (max 60 mg/kg/j). < 10 kg: 7,5 mg/kg/6h",
          contrIndications: "Insuffisance hépatique sévère",
          effetsSecondaires: "Rares si posologie respectée. Hépatotoxicité si surdosage",
          voie: "IV / Per os / Rectal"
        },
        {
          name: "Ibuprofène (Advil®, Nureflex®)",
          indication: "Fièvre résistante, douleur inflammatoire",
          preparation: "Per os",
          posologie: "10 mg/kg/6-8h (max 30 mg/kg/j)",
          contrIndications: "< 3 mois, déshydratation, varicelle, AINS-sensible, ulcère gastrique",
          effetsSecondaires: "Troubles digestifs, insuffisance rénale si déshydratation",
          voie: "Per os",
          alert: "Ne pas utiliser si déshydratation ou varicelle"
        }
      ]
    },
    antibiotiques: {
      name: "Antibiotiques",
      icon: <Shield className="w-5 h-5" />,
      color: "bg-green-500/10 text-green-400 border-green-500/30",
      medications: [
        {
          name: "Amoxicilline-Acide clavulanique (Augmentin®)",
          indication: "Infections bactériennes",
          preparation: "À reconstituer",
          posologie: "100 mg/kg/j en 3 prises (dose amoxicilline). Max 3g/j",
          effetsSecondaires: "Diarrhée, éruption cutanée, allergie",
          voie: "IV / Per os"
        },
        {
          name: "Ceftriaxone (Rocéphine®)",
          indication: "Méningite, infections sévères",
          preparation: "À reconstituer",
          posologie: "50-100 mg/kg/j en 1-2 injections (max 2-4g/j selon pathologie)",
          contrIndications: "Ictère néonatal, hyperbilirubinémie",
          effetsSecondaires: "Réaction allergique, diarrhée, précipitation biliaire",
          voie: "IV / IM",
          alert: "Ne pas mélanger avec calcium"
        }
      ]
    },
    antidotes: {
      name: "Antidotes",
      icon: <Shield className="w-5 h-5" />,
      color: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
      medications: [
        {
          name: "Naloxone (Narcan®)",
          indication: "Surdosage opioïdes",
          preparation: "Pur",
          posologie: "0,01-0,1 mg/kg IV. Titrer toutes les 2-3 min jusqu'à FR > 10/min",
          effetsSecondaires: "Syndrome de sevrage, agitation, vomissements, tachycardie",
          voie: "IV / IM / Intranasale",
          alert: "Durée d'action courte - Surveillance prolongée"
        },
        {
          name: "Flumazénil (Anexate®)",
          indication: "Surdosage benzodiazépines",
          preparation: "Pur",
          posologie: "0,01 mg/kg (max 0,2 mg) toutes les 60 sec jusqu'à éveil",
          contrIndications: "Épilepsie, co-intoxication ADT ou pro-convulsivants",
          effetsSecondaires: "Convulsions, agitation, anxiété",
          voie: "IV",
          alert: "Risque convulsions - Prudence"
        },
        {
          name: "Acétylcystéine (Hidonac®)",
          indication: "Intoxication paracétamol",
          preparation: "À diluer",
          posologie: "Dose de charge: 150 mg/kg sur 1h. 2ème: 50 mg/kg/4h. 3ème: 100 mg/kg/16h",
          effetsSecondaires: "Réactions anaphylactoïdes, nausées",
          voie: "IV",
          alert: "Débuter dans les 8h pour efficacité maximale"
        },
        {
          name: "Atropine Sulfate",
          indication: "Bradycardie, intoxication organophosphorés",
          preparation: "Pur",
          posologie: "0,02 mg/kg (min 0,1 mg) toutes les 3-5 min. Max dose unique: 0,5 mg",
          effetsSecondaires: "Tachycardie, mydriase, rétention urinaire, sécheresse buccale",
          voie: "IV / Intraosseuse"
        }
      ]
    },
    electrolytes: {
      name: "Électrolytes & Correcteurs",
      icon: <Droplet className="w-5 h-5" />,
      color: "bg-blue-500/10 text-blue-400 border-blue-500/30",
      medications: [
        {
          name: "Glucose 10%",
          indication: "Hypoglycémie",
          preparation: "Prêt à l'emploi",
          posologie: "2-5 mL/kg (0,2-0,5 g/kg) en IVL. Répéter selon glycémie",
          effetsSecondaires: "Hyperglycémie, phlébite",
          voie: "IV"
        },
        {
          name: "Bicarbonate de sodium 4,2%",
          indication: "Acidose métabolique sévère, hyperkaliémie",
          preparation: "Prêt à l'emploi",
          posologie: "1-2 mEq/kg (1-2 mL/kg) en IVL. Répéter selon gasométrie",
          effetsSecondaires: "Alcalose, hypernatrémie, hypocalcémie",
          voie: "IV",
          alert: "Pas d'utilisation systématique en AC"
        },
        {
          name: "Calcium Chloride 10%",
          indication: "Hyperkaliémie, hypocalcémie, intoxication inhibiteurs calciques",
          preparation: "À diluer",
          posologie: "0,2 mL/kg (20 mg/kg) en IVL sur 5-10 min",
          contrIndications: "Hypercalcémie, digitalisation",
          effetsSecondaires: "Bradycardie, arythmies, nécrose tissulaire si extravasation",
          voie: "IV / Intraosseuse",
          alert: "Très veinotoxique - Voie centrale préférée"
        }
      ]
    },
    respiratory: {
      name: "Respiratoires",
      icon: <Activity className="w-5 h-5" />,
      color: "bg-teal-500/10 text-teal-400 border-teal-500/30",
      medications: [
        {
          name: "Salbutamol (Ventoline®)",
          indication: "Bronchospasme, asthme aigu",
          preparation: "Solution pour nébulisation",
          posologie: "0,15 mg/kg (max 5 mg) en nébulisation. Répéter toutes les 20 min si besoin",
          effetsSecondaires: "Tachycardie, tremblements, hypokaliémie",
          voie: "Inhalation / Nébulisation"
        },
        {
          name: "Ipratropium (Atrovent®)",
          indication: "Bronchospasme sévère (associé au salbutamol)",
          preparation: "Solution pour nébulisation",
          posologie: "< 30 kg: 250 µg. > 30 kg: 500 µg. En nébulisation avec salbutamol",
          voie: "Inhalation / Nébulisation"
        },
        {
          name: "Dexaméthasone",
          indication: "Croup, asthme sévère, œdème cérébral",
          preparation: "Pur",
          posologie: "Croup: 0,15-0,6 mg/kg PO/IV (max 10 mg). Œdème: 0,5-1 mg/kg",
          voie: "IV / IM / Per os"
        }
      ]
    }
  };

  const handleAiAnalysis = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Veuillez décrire la situation clinique");
      return;
    }

    setLoading(true);
    try {
      const context = selectedMed 
        ? `Médicament sélectionné: ${selectedMed.name}\n\nSituation clinique:\n${aiPrompt}`
        : `Situation clinique pédiatrique:\n${aiPrompt}`;

      const response = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: context,
          context: "prescription_pediatrique"
        })
      });

      if (!response.ok) throw new Error('Erreur lors de l\'analyse IA');

      const data = await response.json();
      setAiResponse(data.response);
      toast.success("Analyse IA terminée");
    } catch (error) {
      console.error('AI Analysis Error:', error);
      toast.error("Erreur lors de l'analyse IA");
      setAiResponse("Erreur lors de l'analyse. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = Object.entries(medicationCategories).reduce((acc, [key, category]) => {
    const filteredMeds = category.medications.filter(med =>
      med.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      med.indication.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filteredMeds.length > 0) {
      acc[key] = { ...category, medications: filteredMeds };
    }
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="prescription-pediatrie-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold glow-text flex items-center gap-3">
            <Baby className="w-8 h-8 text-cyan-400" />
            Prescrire - Pédiatrie
          </h1>
          <p className="text-[hsl(var(--text-dim))] mt-2">
            Guide interactif de prescription pédiatrique avec analyse IA
          </p>
        </div>
      </div>

      {/* Alert Pédiatrique */}
      <Alert className="bg-cyan-500/10 border-cyan-500/30">
        <Scale className="w-5 h-5 text-cyan-400" />
        <AlertDescription className="text-cyan-300">
          <strong>Important :</strong> Toutes les posologies sont à adapter selon le poids et l'âge de l'enfant. 
          Vérifier les doses maximales et les contre-indications spécifiques pédiatriques.
        </AlertDescription>
      </Alert>

      {/* Search */}
      <Card className="border-[hsl(var(--border))]">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-5 h-5 text-[hsl(var(--text-dim))]" />
            <Input
              placeholder="Rechercher un médicament ou une indication..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Medications List */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Baby className="w-5 h-5 text-cyan-400" />
                Médicaments Pédiatriques par Catégorie
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="space-y-2">
                {Object.entries(filteredCategories).map(([key, category]) => (
                  <AccordionItem key={key} value={key} className="border border-[hsl(var(--border))] rounded-lg px-4">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${category.color}`}>
                          {category.icon}
                        </div>
                        <div className="text-left">
                          <div className="font-semibold">{category.name}</div>
                          <div className="text-sm text-[hsl(var(--text-dim))]">
                            {category.medications.length} médicament(s)
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 mt-3">
                        {category.medications.map((med, idx) => (
                          <div
                            key={idx}
                            className="p-4 bg-[hsl(var(--card-hover))] rounded-lg border border-[hsl(var(--border))] hover:border-cyan-500/50 transition-colors cursor-pointer"
                            onClick={() => setSelectedMed(med)}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-semibold text-white">{med.name}</h4>
                              {med.alert && (
                                <Badge variant="destructive" className="ml-2">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Alert
                                </Badge>
                              )}
                            </div>
                            
                            <div className="space-y-2 text-sm">
                              <div className="flex gap-2">
                                <Info className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                                <div>
                                  <span className="text-[hsl(var(--text-dim))]">Indication:</span>{' '}
                                  <span className="text-white">{med.indication}</span>
                                </div>
                              </div>
                              
                              <div className="flex gap-2">
                                <Scale className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                                <div>
                                  <span className="text-[hsl(var(--text-dim))]">Posologie:</span>{' '}
                                  <span className="text-white">{med.posologie}</span>
                                </div>
                              </div>
                              
                              <div className="flex gap-2">
                                <Clock className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                                <div>
                                  <span className="text-[hsl(var(--text-dim))]">Voie:</span>{' '}
                                  <Badge variant="outline" className="ml-1">{med.voie}</Badge>
                                </div>
                              </div>

                              {med.alert && (
                                <Alert className="mt-2 bg-red-500/10 border-red-500/30">
                                  <AlertTriangle className="w-4 h-4 text-red-400" />
                                  <AlertDescription className="text-red-300 text-xs">
                                    {med.alert}
                                  </AlertDescription>
                                </Alert>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>

        {/* AI Analysis Panel */}
        <div className="space-y-4">
          {selectedMed && (
            <Card className="border-cyan-500/30 bg-cyan-500/5">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Baby className="w-4 h-4" />
                  Sélectionné
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <h3 className="font-bold text-white">{selectedMed.name}</h3>
                  <div className="text-sm space-y-2">
                    <div>
                      <span className="text-[hsl(var(--text-dim))]">Préparation:</span>
                      <p className="text-white">{selectedMed.preparation}</p>
                    </div>
                    {selectedMed.contrIndications && (
                      <div>
                        <span className="text-[hsl(var(--text-dim))] flex items-center gap-1">
                          <Lock className="w-3 h-3" />
                          Contre-indications:
                        </span>
                        <p className="text-red-300 text-xs">{selectedMed.contrIndications}</p>
                      </div>
                    )}
                    {selectedMed.effetsSecondaires && (
                      <div>
                        <span className="text-[hsl(var(--text-dim))] flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Effets secondaires:
                        </span>
                        <p className="text-yellow-300 text-xs">{selectedMed.effetsSecondaires}</p>
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={() => setSelectedMed(null)}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    Désélectionner
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-cyan-400" />
                Analyse IA Pédiatrique
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="Décrivez la situation clinique pédiatrique (âge, poids, pathologie, antécédents...)&#10;&#10;Exemple: Enfant 5 ans, 18kg, convulsion > 5 min, pas d'antécédent épileptique connu"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                rows={6}
                className="resize-none"
              />
              
              <Button
                onClick={handleAiAnalysis}
                disabled={loading || !aiPrompt.trim()}
                className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Analyse en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyser avec IA
                  </>
                )}
              </Button>

              {aiResponse && (
                <div className="p-4 bg-[hsl(var(--card-hover))] rounded-lg border border-cyan-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <Brain className="w-5 h-5 text-cyan-400" />
                    <span className="font-semibold text-white">Recommandation IA</span>
                  </div>
                  <div className="text-sm text-white whitespace-pre-wrap leading-relaxed">
                    {aiResponse}
                  </div>
                  <Alert className="mt-3 bg-yellow-500/10 border-yellow-500/30">
                    <AlertTriangle className="w-4 h-4 text-yellow-400" />
                    <AlertDescription className="text-yellow-300 text-xs">
                      Cette analyse IA est une aide à la décision pour la prescription pédiatrique. 
                      Elle ne remplace pas le jugement clinique du médecin. Toujours adapter selon l'enfant.
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
